import { useEffect } from 'react';
import { Pagination } from '@quero-delivery/quero-components-web';
import type { SubmitHandler } from 'react-hook-form';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { format } from '@querodelivery/qd-date-helper';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import * as Form from '@radix-ui/react-form';
import { Button, Switch, Text, useTheme } from '@querodelivery/uni-react';
import styles from './MembersList.module.scss';
import { DEFAULT_LIMIT_REQUEST } from '@/Constants';
import type { IDropdownOptions, IMember } from '@/Interfaces/Data';
import JsonView from '@/Shared/JsonView';
import { useAppDispatch, useAppSelector } from '@/_store/hooks';
import {
  membersRequest,
  selectMembers,
  setMemberIsActiveRequest,
} from '@/_store/modules/members';
import { useCities } from '@/_hooks/use-cities';
import { TableSkeleton } from '@/_components/skeletons/table-skeleton/table-skeleton';
import { TextInput } from '@/_components/text-input/text-input';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { Table, useStackedTableLayout } from '@/_components/table';
import { TableEmptyState } from '@/_components/table-empty-state/table-empty-state';

const fetchMembersFormSchema = yup.object({
  name: yup.string().optional(),
  city: yup.string().optional(),
  status: yup.boolean().optional(),
});

type TypePostMemberForm = yup.InferType<typeof fetchMembersFormSchema>;

const limit = DEFAULT_LIMIT_REQUEST;
const GRID_TEMPLATE_COLUMNS = '2fr 1fr 2fr 2fr 2fr 2fr';

const STATUS_OPTIONS: IDropdownOptions[] = [
  { value: 'true', text: 'Ativado', key: 'true' },
  { value: 'false', text: 'Desativado', key: 'false' },
];

function MembersList() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isStackedTableLayout = useStackedTableLayout();
  const { color } = useTheme();

  const members = useAppSelector(selectMembers);
  const loading = useAppSelector(state => state.members.fetching);

  const page = useAppSelector(state => state.members.page);
  const totalCount = useAppSelector(state => state.members.totalCount);

  const { CITIES_OPTIONS_WITH_ALL, loadingCities, getCityInfo } = useCities();

  const {
    getValues,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<TypePostMemberForm>({
    resolver: yupResolver(fetchMembersFormSchema),
    defaultValues: {
      city: '',
      name: '',
      status: undefined,
    },
  });

  useEffect(() => {
    dispatch(membersRequest({}));
  }, []);

  const handleChangeStatus = (member: IMember) => {
    dispatch(
      setMemberIsActiveRequest({
        memberId: member._id,
        isActive: !member.isAtivo,
      }),
    );
  };
  const submitFetchMembersForm: SubmitHandler<TypePostMemberForm> = ({
    city,
    name,
    status,
  }) => {
    dispatch(
      membersRequest({
        page: 1,
        name,
        cityId: city,
        isActive: status,
      }),
    );
  };

  const handleChangePage = (page: number) => {
    const { name, city } = getValues();
    dispatch(
      membersRequest({
        page,
        name,
        cityId: city,
      }),
    );
  };

  const navigateToMemberDetails = (memberId: string) => {
    navigate(`/equipe/${memberId}`);
  };

  function renderRow(member: IMember) {
    const citiesInfo = member.municipioIds?.map(e => getCityInfo(e?._id));
    return (
      <Table.Row
        key={member._id}
        onClick={() => navigateToMemberDetails(member._id)}
        gridTemplateColumns={GRID_TEMPLATE_COLUMNS}
      >
        <Table.Cell className={styles.tcMember}>
          <Text
            fontWeight="semibold"
            tone="supporting"
            style={{
              overflowWrap: 'break-word',
              wordBreak: 'break-word',
              minWidth: 0,
            }}
          >
            {member.nome}
          </Text>
        </Table.Cell>
        <Table.Cell>
          {member.sigla && (
            <Text as="span" className={styles.sigla}>
              {member.sigla}
            </Text>
          )}
        </Table.Cell>
        <Table.Cell>
          <Text>
            {citiesInfo?.map(city => `${city?.nome} - ${city?.uf}`).join('; ')}
          </Text>
        </Table.Cell>
        <Table.Cell className={styles.statusColumn}>
          <Switch
            label=""
            variant="success-warning"
            checked={member.isAtivo}
            onClick={event => {
              event.stopPropagation();
              handleChangeStatus(member);
            }}
            size="lg"
            checkedColor={color['color-success-fill']}
            uncheckedColor={color['color-danger-fill']}
          />
        </Table.Cell>
        <Table.Cell>
          <Text>
            {member.createdAt
              ? format(new Date(member.createdAt), 'dd/MM/yyyy HH:mm')
              : ''}
          </Text>
        </Table.Cell>
        <Table.Cell>
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigateToMemberDetails(member._id)}
          >
            <Text className={styles.editAccessText}>Editar acesso</Text>
          </Button>
        </Table.Cell>
      </Table.Row>
    );
  }

  function renderMemberMobileCard(member: IMember) {
    return (
      <Table.MobileCard
        key={member._id}
        onClick={() => navigateToMemberDetails(member._id)}
      >
        <Table.MobileItem label="Nome">
          <Text fontWeight="semibold" tone="supporting">
            {member.nome}
          </Text>
          {member.sigla && (
            <Text as="span" className={`${styles.sigla} ${styles.mobileSigla}`}>
              {member.sigla}
            </Text>
          )}
        </Table.MobileItem>
        {member?.municipioIds?.length && member.municipioIds?.length > 0 ? (
          <Table.MobileItem label="Municípios">
            <Text>
              {member.municipioIds
                ?.map(e => getCityInfo(e?._id))
                .map(city => `${city?.nome} - ${city?.uf}`)
                .join('; ')}
            </Text>
          </Table.MobileItem>
        ) : null}
        <Table.MobileItem label="Status">
          <Switch
            variant="success-warning"
            label=""
            checked={member.isAtivo}
            onClick={() => handleChangeStatus(member)}
            size="lg"
            checkedColor={color['color-success-fill']}
            uncheckedColor={color['color-danger-fill']}
          />
        </Table.MobileItem>
        <Table.MobileItem label="Criado em">
          <Text>
            {member.createdAt
              ? format(new Date(member.createdAt), 'dd/MM/yyyy HH:mm')
              : ''}
          </Text>
        </Table.MobileItem>
        <Table.MobileItem label="">
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate(`/equipe/${member._id}`)}
          >
            <Text>Editar acesso</Text>
          </Button>
        </Table.MobileItem>
      </Table.MobileCard>
    );
  }

  return (
    <div className="container">
      <Form.Root onSubmit={handleSubmit(submitFetchMembersForm)}>
        <div className={styles.filterContainer}>
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextInput
                name="name"
                value={value}
                errorMessage={errors.name?.message}
                label="Colaborador"
                onChange={event => onChange(event.target.value)}
                autoFocus
              />
            )}
          />

          <Controller
            name="city"
            control={control}
            render={({ field: { onChange, value } }) => (
              <DropdownInput
                errorMessage={errors.city?.message}
                label="Município"
                options={CITIES_OPTIONS_WITH_ALL}
                loading={loadingCities}
                value={value}
                search
                clearable
                placeholder="Selecione o município"
                onChange={onChange}
              />
            )}
          />

          <Controller
            name="status"
            control={control}
            render={({ field: { onChange, value } }) => {
              return (
                <DropdownInput
                  clearable
                  label="Status"
                  options={STATUS_OPTIONS}
                  value={value ? String(value) : ''}
                  placeholder="Filtrar por status"
                  onChange={onChange}
                  errorMessage={errors.status?.message}
                />
              );
            }}
          />
        </div>
        <div className={styles.buttonsContainer}>
          <Button variant="cta" size="lg" type="submit" disabled={loading}>
            buscar
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate('/equipe/cadastro')}
          >
            cadastrar colaborador
          </Button>
        </div>
      </Form.Root>

      <div className="mgn-top-sm">
        {loading ? (
          <TableSkeleton columnCount={5} rowCount={5} />
        ) : members && members.length > 0 ? (
          <>
            <Text
              variant="headingMd"
              tone="supporting"
              className={styles.headerLabel}
            >
              {totalCount} {totalCount === 1 ? 'colaborador' : 'colaboradores'}
            </Text>
            {isStackedTableLayout ? (
              <Table.MobileList className={styles.membersTable}>
                {members.map(member => renderMemberMobileCard(member))}
              </Table.MobileList>
            ) : (
              <Table className={styles.membersTable}>
                <Table.Header>
                  <Table.Row gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
                    <Table.HeaderCell>Nome</Table.HeaderCell>
                    <Table.HeaderCell />
                    <Table.HeaderCell>Município</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                    <Table.HeaderCell>Data de cadastro</Table.HeaderCell>
                    <Table.HeaderCell />
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {members!.map(member => renderRow(member))}
                </Table.Body>
              </Table>
            )}
            <div className={styles.pagination}>
              <Pagination
                size="sm"
                limitPerPage={limit}
                currentPage={page}
                totalCount={totalCount || 0}
                onPageChange={handleChangePage}
              />
            </div>
          </>
        ) : (
          <TableEmptyState
            title="Nenhum colaborador encotrado"
            description="Não encontramos colaboradores com os filtros selecionados."
          />
        )}
      </div>

      <footer className="end-page-margin" />

      <JsonView name="Members" source={members} />
    </div>
  );
}

export default MembersList;
